import axios from "axios";
import { format } from "date-fns";
import {
  IActionAddCoefficient,
  IActionBulkCoefficient,
  IActionEditCoefficient,
} from "../../../types/contractsInterface";
import { AppDispatch } from "../../reducer/store";
import { getToken } from "../axiosToken";
import { formatErrorMessage } from "../../../utils/formatError";
import {
  reducerGetBudgetPoses,
  reducerGetCoefficientList,
  reducerSetRequestStatus,
  reducerSetRequestStatusMessage,
} from "../../reducer/contracts/reducers/reducerCoefficientList";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const buildCoefficientPayload = (data: IActionAddCoefficient) => {
  const contractId = data.contract ? parseInt(data.contract) : 0;

  return {
    project: {
      id: data.project?.value,
      name: data.project?.label,
    },
    contract: {
      id: contractId,
    },
    coefficient: parseFloat(data.coefficient.replace(",", ".")),
    startDate: data.startDate ? format(data.startDate, "yyyy-MM-dd") : undefined,
    ...(data.endDate ? { endDate: format(data.endDate, "yyyy-MM-dd") } : {}),
    ...(data.pos
      ? {
          pos: {
            id: data.pos.value,
            name: data.pos.label,
          },
        }
      : {}),
  };
};

const handleCoefficientRequestError = (dispatch: AppDispatch, e: any) => {
  if (e.response && e.response.status === 401) {
    window.location.href = "/login";
    return;
  }

  if (e.response && e.response.status === 400) {
    dispatch(reducerSetRequestStatus(400));
    dispatch(
      reducerSetRequestStatusMessage(
        formatErrorMessage(e.response?.data) ||
          "Запись с таким проектом и периодом уже существует"
      )
    );
  }
};

export const actionAddCoefficient =
  (data: IActionAddCoefficient) =>
  async (dispatch: AppDispatch, getState: () => any) => {
    const { token } = await getToken();
    axios.defaults.headers.common["Authorization"] = token;
    const contractId = data.contract ? parseInt(data.contract) : 0;

    try {
      const { status } = await axios.patch(
        `${BASE_URL}/json/contracts/coefficients`,
        buildCoefficientPayload(data)
      );
      dispatch(reducerSetRequestStatusMessage(null));
      dispatch(reducerSetRequestStatus(status));
      dispatch(actionGetCoefficientList(contractId));
      return true;
    } catch (e: any) {
      handleCoefficientRequestError(dispatch, e);
      return false;
    }
  };

export const actionEditCoefficient =
  (data: IActionEditCoefficient) => async (dispatch: AppDispatch, getState: () => any) => {
    const { token } = await getToken();
    axios.defaults.headers.common["Authorization"] = token;
    const contractId = data.contract ? parseInt(data.contract) : 0;

    try {
      const { status } = await axios.patch(
        `${BASE_URL}/json/contracts/coefficients`,
        {
          contractBudget: {
            id: data.contractId,
          },
          ...buildCoefficientPayload(data),
        }
      );
      dispatch(reducerSetRequestStatusMessage(null));
      dispatch(reducerSetRequestStatus(status));
      dispatch(actionGetCoefficientList(contractId));
    } catch (e: any) {
      handleCoefficientRequestError(dispatch, e);
      if (!e.response || e.response.status !== 400) {
        dispatch(reducerSetRequestStatus("updateError"));
      }
    }
  };

export const actionDeleteCoefficient =
  (data: any) => async (dispatch: AppDispatch, getState: () => any) => {
    const { token } = await getToken();
    axios.defaults.headers.common["Authorization"] = token;
    const contractId = data.contract ? parseInt(data.contract) : 0;

    try {
      const { status } = await axios.delete(
        `${BASE_URL}/json/contracts/coefficients/${data.contractId}`);
      dispatch(reducerSetRequestStatus(status));
      dispatch(actionGetCoefficientList(contractId));
    } catch (e: any) {
      if (e.response && e.response.status === 401) {
        window.location.href = "/login";
      }
    }
  };

export const actionGetCoefficientList =
  (contractId: number | string, limit?: number, page?: number) =>
  async (dispatch: AppDispatch, getState: () => any) => {
    const { token } = await getToken();
    axios.defaults.headers.common["Authorization"] = token;

    const { filterInputValues, page: storedPage, limit: storedLimit } =
      getState().contracts.coefficients;
    const actualLimit =
      limit ??
      JSON.parse(localStorage.getItem("pageLimit") as string) ??
      storedLimit ??
      10;
    const actualPage = page ?? storedPage ?? 1;
    const requestBody: Record<string, unknown> = {
      contract: {
        id: parseInt(String(contractId)),
      },
      page: {
        page: actualPage,
        limit: actualLimit,
      },
    };

    if (filterInputValues.project?.value) {
      requestBody.project = { id: filterInputValues.project.value };
    }
    if (filterInputValues.posSearch) {
      requestBody.posSearch = filterInputValues.posSearch;
    }
    if (filterInputValues.coefficient) {
      requestBody.coefficient = filterInputValues.coefficient;
    }
    if (filterInputValues.fromDate) {
      requestBody.fromDate = filterInputValues.fromDate;
    }
    if (filterInputValues.toDate) {
      requestBody.toDate = filterInputValues.toDate;
    }
    if (filterInputValues.activated === "true") {
      requestBody.activated = true;
    } else if (filterInputValues.activated === "false") {
      requestBody.activated = false;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/json/contracts/coefficients`,
        requestBody
      );

      dispatch(
        reducerGetCoefficientList({
          ...response.data,
          page: actualPage,
          limit: actualLimit,
        })
      );
    } catch (e: any) {
      if (e.response && e.response.status === 401) {
        window.location.href = "/login";
      }
    }
  };

export const actionGetBudgetPoses =
  (contractId: number | string, search?: string) =>
  async (dispatch: AppDispatch) => {
    const { token } = await getToken();
    axios.defaults.headers.common["Authorization"] = token;

    try {
      const response = await axios.get(
        `${BASE_URL}/json/contracts/coefficients/poses`,
        {
          params: {
            contract: contractId,
            ...(search ? { search } : {}),
          },
        }
      );

      dispatch(reducerGetBudgetPoses(response.data));
    } catch (e: any) {
      if (e.response && e.response.status === 401) {
        window.location.href = "/login";
      }
    }
  };

export const actionActivateCoefficients =
  (data: IActionBulkCoefficient) =>
  async (dispatch: AppDispatch) => {
    const { token } = await getToken();
    axios.defaults.headers.common["Authorization"] = token;

    try {
      await axios.post(
        `${BASE_URL}/json/contracts/coefficients/activate`,
        {
          contract: {
            id: data.contractId,
          },
          ids: data.ids,
        }
      );
      dispatch(reducerSetRequestStatus("activateSuccess"));
      dispatch(actionGetCoefficientList(data.contractId));
    } catch (e: any) {
      if (e.response && e.response.status === 401) {
        window.location.href = "/login";
      }
      if (e.response && e.response.status === 400) {
        dispatch(reducerSetRequestStatus(400));
      } else {
        dispatch(reducerSetRequestStatus("activateError"));
      }
    }
  };

export const actionBulkDeleteCoefficients =
  (data: IActionBulkCoefficient) =>
  async (dispatch: AppDispatch) => {
    const { token } = await getToken();
    axios.defaults.headers.common["Authorization"] = token;

    try {
      await axios.post(
        `${BASE_URL}/json/contracts/coefficients/delete`,
        {
          contract: {
            id: data.contractId,
          },
          ids: data.ids,
        }
      );
      dispatch(reducerSetRequestStatus("bulkDeleteSuccess"));
      dispatch(actionGetCoefficientList(data.contractId));
    } catch (e: any) {
      if (e.response && e.response.status === 401) {
        window.location.href = "/login";
      }
      if (e.response && e.response.status === 400) {
        dispatch(reducerSetRequestStatus(400));
      } else {
        dispatch(reducerSetRequestStatus("bulkDeleteError"));
      }
    }
  };

export const actionImportCoefficients =
    (contractId?: number, files?: File[]) => async (dispatch: AppDispatch) => {
        const {token} = await getToken();
        const headers = {
            Authorization: token,
            "Content-Type": "multipart/form-data",
        };

        const formData = new FormData();

        files && files.forEach((file: File) => {
            formData.append("excelFile", file, file.name);
        });

        try {
            const response = await axios.put(
                `${BASE_URL}/json/contracts/coefficients/import`,
                formData,
                {
                    headers: headers,
                }
            );
            dispatch(reducerSetRequestStatus(response.status));
            if (contractId) {
                dispatch(actionGetCoefficientList(contractId));
            }
        } catch (e: any) {
            if (e.response && e.response.status === 401) {
                window.location.href = "/login";
            }
            if (e.response && e.response.status === 400) {
                dispatch(reducerSetRequestStatus(400));
            }
            if (e.response && e.response.status === 404) {
                dispatch(reducerSetRequestStatus(404));
            }
        }
    };